import { Box, Divider, Typography } from "@mui/material";
import type { JSX } from "react";

import { ChatActivity } from "./chat/ChatActivity.js";
import { HarvesterBrain } from "./HarvesterBrain.js";
import { MissionsList } from "./MissionsList.js";

/**
 * The agent's live "consciousness" — what it is thinking / doing RIGHT NOW.
 *
 * **This used to be its own `/sales/brain` tab; it was folded into the bottom
 * of the Status tab 2026-08-07** so an operator reads one page top-to-bottom
 * ("who is working" → "what it costs" → "how far through the map" → "what it
 * is doing this second") instead of hopping between two tabs that answer the
 * same question. `/sales/brain` now redirects to `/sales/status`.
 *
 * **Mode-aware** (2026-06-19) — the caller passes which track(s) the operator
 * has switched on, so flipping the mode switches the brain too:
 *
 *   - **leads** → the lead agent's Plan/Act/Observe/Reflect transcript
 *     (`ChatActivity`, chat-dominant) + the **Mission backlog** (`MissionsList`)
 *     as a right sidebar at `xl`.
 *   - **contacts** → the contact-harvester's live enumerate/dig/dedup feed
 *     (`HarvesterBrain`) — the lead transcript is meaningless for the harvester.
 *   - **both** → both, stacked, each under its own heading.
 *
 * Each feed scrolls inside its own box so the section can't run away with the
 * page height.
 */
export function BrainSection({
  showLeads,
  showContacts,
}: {
  showLeads: boolean;
  showContacts: boolean;
}): JSX.Element | null {
  const both = showLeads && showContacts;

  if (!showLeads && !showContacts) return null;

  const leadBrain = (
    <Box
      sx={{
        display: "grid",
        gap: 2.5,
        alignItems: "stretch",
        gridTemplateColumns: { xl: "minmax(0, 1.8fr) minmax(340px, 1fr)" },
      }}
    >
      <ChatActivity />
      <Box sx={{ minWidth: 0, maxHeight: { xl: "calc(100dvh - 232px)" }, overflowY: { xl: "auto" } }}>
        <MissionsList />
      </Box>
    </Box>
  );

  // Single-track: render just that track's brain (no section headers needed).
  const body = !both ? (
    showContacts ? <HarvesterBrain /> : leadBrain
  ) : (
    // Both tracks on: stack them under clear headings.
    <Box sx={{ display: "grid", gap: 3 }}>
      <Box>
        <SectionLabel>Lead agent</SectionLabel>
        {leadBrain}
      </Box>
      <Divider />
      <Box>
        <SectionLabel>Contact harvester</SectionLabel>
        <HarvesterBrain />
      </Box>
    </Box>
  );

  return (
    <Box component="section" data-testid="brain-section" sx={{ display: "grid", gap: 1.5 }}>
      <SectionLabel>What the agent is doing right now</SectionLabel>
      {body}
    </Box>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Typography
      component="h2"
      sx={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}
    >
      {children}
    </Typography>
  );
}
